// ADR 16.9.26n §2 — one collapse, two regions.
//
// 8.9.26i §4's `useCollapsedSidebar` generalised over `(key, region)`, because
// the list pane folds for the same reason and from the same place: `Sidebar`
// and `Rail` are siblings chosen by `LeadingChrome`, and the list cell and the
// strip that replaces it are siblings chosen by `ShellBody`. A component
// cannot swap itself for its sibling, so the state and its persistence live
// one level up, in the shell — and the shell now has two of them.
//
// The hook takes **both** the key and the region although the key spells the
// region. They are two contracts with two parties: the key is a promise to the
// learner's browser (a stored preference must survive any rename on our side)
// and the region is a wire vocabulary Infra reads back by name
// (`feature-usage.toml`'s `region_state` breakdown). Deriving one from the
// other would make the next telemetry rename silently orphan every stored
// preference. Two callers keeping two strings in step is the cheaper risk.
//
// Filed in `layout/` rather than `ui/`: the logic is the shell's, `ui/` is a
// design system at its 15-file cap, and this file spells no size-class string
// — it needs none of 6.9.26p AC6's exemption.

import { useState } from 'react'

import { emitFoldToggled, type ChromeRegion } from '../telemetry/chromeTelemetry'

/**
 * A persisted boolean collapse for one chrome region.
 *
 * Wrapped in `try/catch` for the same reason 5.9.26u §1's fold is: a blocked
 * store renders the default (open) rather than a blank page, and a write that
 * fails still holds for this session.
 */
export function useCollapsedChrome(
  key: string,
  region: ChromeRegion,
): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(key) === 'true'
    } catch {
      return false
    }
  })
  return [
    collapsed,
    (next: boolean) => {
      setCollapsed(next)
      // ADR 9.9.26m §2b — neither region is a `Disclosure`, so each emits from
      // where its state lives. Note the negation: the state variable is
      // `collapsed` and the wire property is `open`, because one vocabulary
      // across every region beats each region's local spelling — a series
      // reading backwards from every other series is a trap.
      emitFoldToggled(region, !next)
      try {
        window.localStorage.setItem(key, String(next))
      } catch {
        // The collapse still holds for this session.
      }
    },
  ]
}
